import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { API_BASE_URL } from '../../config';
import { FeedItem, User, Club } from '../../types';
import VoiceNoteCard from './VoiceNoteCard';
import PhotoPostCard from './PhotoPostCard';
import RideStoryCard from './RideStoryCard';
import RideStoryDetail from './RideStoryDetail';
import FeedSidebarProfile from './FeedSidebarProfile';
import FeedSidebarArticles from './FeedSidebarArticles';
import './ClubFeed.css';

interface ClubFeedProps {
  isLoaded: boolean;
  user: User;
  userClubs: Club[];
  onViewArticle?: (slug: string) => void;
  onViewAll?: () => void;
  onNavigateToClubs?: () => void;
}

const ClubFeed: React.FC<ClubFeedProps> = ({ isLoaded, user, userClubs, onViewArticle, onViewAll, onNavigateToClubs }) => {
  const { t } = useLanguage();
  const [items, setItems] = useState<FeedItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const [showClubDropdown, setShowClubDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const [selectedStory, setSelectedStory] = useState<FeedItem | null>(null);

  const [selectedClubId, setSelectedClubId] = useState<number | null>(() => {
    const stored = localStorage.getItem('selected_club_id');
    return stored ? parseInt(stored, 10) : null;
  });

  // Compose state
  const [showCompose, setShowCompose] = useState(false);
  const [composeText, setComposeText] = useState('');
  const [composeImage, setComposeImage] = useState<File | null>(null);
  const [composeImagePreview, setComposeImagePreview] = useState<string | null>(null);
  const [composing, setComposing] = useState(false);
  const [composeError, setComposeError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleDeletePost = useCallback(async (postId: number) => {
    if (!window.confirm(t('feed.menu.deleteConfirm'))) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/feed/posts/${postId}`, {
        method: 'DELETE', credentials: 'include',
      });
      if (res.ok) {
        setItems(prev => prev.filter(i => i.id !== postId));
      }
    } catch { /* ignore */ }
  }, [t]);

  // Auto-select first club if none selected
  useEffect(() => {
    if (userClubs.length > 0 && selectedClubId === null) {
      const firstId = userClubs[0].id;
      setSelectedClubId(firstId);
      localStorage.setItem('selected_club_id', String(firstId));
    }
  }, [userClubs, selectedClubId]);

  const handleClubChange = useCallback((clubId: number) => {
    setSelectedClubId(clubId);
    localStorage.setItem('selected_club_id', String(clubId));
    setShowClubDropdown(false);
    setItems([]);
    setNextCursor(null);
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowClubDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchFeed = useCallback(async (cursor?: string) => {
    if (cursor) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }
    setError(null);

    try {
      const clubParam = selectedClubId ? `&club_id=${selectedClubId}` : '';
      const url = cursor
        ? `${API_BASE_URL}/api/feed?limit=15${clubParam}&cursor=${cursor}`
        : `${API_BASE_URL}/api/feed?limit=15${clubParam}`;
      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to load');
      const data = await res.json();

      if (cursor) {
        setItems(prev => [...prev, ...(data.items || [])]);
      } else {
        setItems(data.items || []);
      }
      setNextCursor(data.next_cursor);
      setHasMore(data.has_more);
    } catch {
      setError(t('feed.errorLoad'));
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [t, selectedClubId]);

  useEffect(() => {
    if (selectedClubId !== null) {
      fetchFeed();
    }
  }, [selectedClubId, fetchFeed]);

  // Infinite scroll
  useEffect(() => {
    if (observerRef.current) observerRef.current.disconnect();

    observerRef.current = new IntersectionObserver(entries => {
      if (entries[0]?.isIntersecting && hasMore && !loadingMore && nextCursor) {
        fetchFeed(nextCursor);
      }
    }, { threshold: 0.1 });

    if (sentinelRef.current) {
      observerRef.current.observe(sentinelRef.current);
    }

    return () => observerRef.current?.disconnect();
  }, [hasMore, loadingMore, nextCursor, fetchFeed]);

  // Compose handlers
  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setComposeImage(file);
    setComposeImagePreview(URL.createObjectURL(file));
  };

  const handleRemoveImage = () => {
    setComposeImage(null);
    if (composeImagePreview) URL.revokeObjectURL(composeImagePreview);
    setComposeImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleComposeSubmit = async () => {
    const trimmed = composeText.trim();
    if (!trimmed && !composeImage) return;
    if (trimmed.length > 500) return;

    setComposing(true);
    setComposeError(null);

    const formData = new FormData();
    if (trimmed) formData.append('text', trimmed);
    if (composeImage) formData.append('image', composeImage);

    try {
      const res = await fetch(`${API_BASE_URL}/api/feed/posts`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });
      if (!res.ok) throw new Error();

      // Reset compose
      setComposeText('');
      handleRemoveImage();
      setShowCompose(false);

      // Refresh feed
      await fetchFeed();
    } catch {
      setComposeError(t('feed.compose.error'));
    } finally {
      setComposing(false);
    }
  };

  const handleCancelCompose = () => {
    setShowCompose(false);
    setComposeText('');
    handleRemoveImage();
    setComposeError(null);
  };

  const canPost = (composeText.trim().length > 0 || composeImage !== null) && composeText.length <= 500;

  const renderItem = (item: FeedItem) => {
    switch (item.item_type) {
      case 'voice_note':
        return <VoiceNoteCard key={`vn-${item.id}`} item={item} currentUserId={user.id} onDelete={handleDeletePost} />;
      case 'photo_post':
        return <PhotoPostCard key={`pp-${item.id}`} item={item} currentUserId={user.id} onDelete={handleDeletePost} />;
      case 'ride_story':
        return <RideStoryCard key={`rs-${item.id}`} item={item} onClick={() => setSelectedStory(item)} />;
      default:
        return null;
    }
  };

  const renderComposeForm = () => {
    if (!showCompose) return null;

    return (
      <div className="compose-form">
        <textarea
          className="compose-textarea"
          placeholder={t('feed.compose.placeholder')}
          value={composeText}
          onChange={e => setComposeText(e.target.value)}
          rows={3}
          maxLength={500}
          autoFocus
        />

        {composeImagePreview && (
          <div className="compose-image-preview">
            <img src={composeImagePreview} alt="Preview" />
            <button className="compose-image-remove" onClick={handleRemoveImage}>&times;</button>
          </div>
        )}

        <div className="compose-actions">
          <div className="compose-actions-left">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={handleImageSelect}
              style={{ display: 'none' }}
            />
            <button
              className="compose-photo-btn"
              onClick={() => fileInputRef.current?.click()}
              title={t('feed.compose.addPhoto')}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                <circle cx="8.5" cy="8.5" r="1.5"/>
                <polyline points="21 15 16 10 5 21"/>
              </svg>
              {t('feed.compose.addPhoto')}
            </button>
            <span className={`compose-char-count ${composeText.length > 500 ? 'over' : ''}`}>
              {composeText.length}{t('feed.compose.charCount')}
            </span>
          </div>
          <div className="compose-actions-right">
            <button className="compose-cancel-btn" onClick={handleCancelCompose} disabled={composing}>
              {t('feed.compose.cancel')}
            </button>
            <button
              className="compose-submit-btn"
              onClick={handleComposeSubmit}
              disabled={!canPost || composing}
            >
              {composing ? '...' : t('feed.compose.post')}
            </button>
          </div>
        </div>

        {composeError && <div className="compose-error">{composeError}</div>}
      </div>
    );
  };

  const selectedClub = userClubs.find(c => c.id === selectedClubId);

  const renderFeedHeader = () => (
    <div className="club-feed-header">
      <div className="club-feed-title-row">
        <h2 className="club-feed-title">{t('feed.title')}</h2>
        {userClubs.length > 0 && (
          <div className="feed-mode-switcher" ref={dropdownRef}>
            <button
              className="feed-mode-btn"
              onClick={() => setShowClubDropdown(prev => !prev)}
            >
              👥 {selectedClub?.short_name || selectedClub?.name || t('clubs.selectClub')}
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: 4 }}>
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
            {showClubDropdown && (
              <div className="feed-mode-dropdown">
                {userClubs.map(club => (
                  <button
                    key={club.id}
                    className={`feed-mode-dropdown-item ${club.id === selectedClubId ? 'active' : ''}`}
                    onClick={() => handleClubChange(club.id)}
                  >
                    👥 {club.name}
                    {club.id === selectedClubId && (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      <button
        className={`compose-toggle-btn ${showCompose ? 'active' : ''}`}
        onClick={() => showCompose ? handleCancelCompose() : setShowCompose(true)}
        title={t('feed.compose.title')}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <line x1="12" y1="5" x2="12" y2="19"/>
          <line x1="5" y1="12" x2="19" y2="12"/>
        </svg>
      </button>
    </div>
  );

  const renderFeedContent = () => {
    // Onboarding: user has no clubs
    if (userClubs.length === 0) {
      return (
        <div className="club-feed">
          <div className="feed-onboarding">
            <h2 className="feed-onboarding-title">{t('clubs.onboardingTitle')}</h2>
            <p className="feed-onboarding-subtitle">{t('clubs.onboardingSubtitle')}</p>
            <button
              className="feed-onboarding-btn"
              onClick={onNavigateToClubs}
            >
              {t('clubs.onboardingSearch')}
            </button>
            <p className="feed-onboarding-create">{t('clubs.onboardingCreate')}</p>
          </div>
        </div>
      );
    }

    if (loading) {
      return (
        <div className="club-feed">
          {renderFeedHeader()}
          {[1, 2, 3].map(i => (
            <div key={i} className="club-feed-skeleton">
              <div className="skeleton-line short" />
              <div className="skeleton-line medium" />
              <div className="skeleton-line long" />
              <div className="skeleton-line map" />
            </div>
          ))}
        </div>
      );
    }

    if (error && items.length === 0) {
      return (
        <div className="club-feed">
          {renderFeedHeader()}
          <div className="club-feed-error">
            <p>{error}</p>
            <button onClick={() => fetchFeed()}>{t('feed.retry')}</button>
          </div>
        </div>
      );
    }

    return (
      <div className="club-feed">
        {renderFeedHeader()}
        {renderComposeForm()}

        {items.length === 0 ? (
          <div className="club-feed-empty">
            <h3>{t('feed.empty')}</h3>
            <p>{t('feed.emptySubtitle')}</p>
          </div>
        ) : (
          <>
            {items.map(renderItem)}
            {loadingMore && <div className="club-feed-loading">{t('feed.loadMore')}</div>}
            <div ref={sentinelRef} style={{ height: 1 }} />
          </>
        )}
      </div>
    );
  };

  return (
    <div className="feed-layout">
      <aside className="feed-sidebar feed-sidebar-left">
        <FeedSidebarProfile />
      </aside>
      <main className="feed-main">
        {renderFeedContent()}
      </main>
      <aside className="feed-sidebar feed-sidebar-right">
        <FeedSidebarArticles onViewArticle={onViewArticle} onViewAll={onViewAll} />
      </aside>

      {selectedStory && selectedStory.ride_id && (
        <RideStoryDetail
          rideId={selectedStory.ride_id}
          onClose={() => setSelectedStory(null)}
        />
      )}
    </div>
  );
};

export default ClubFeed;
